import { create } from 'zustand';
import { InputValue, NodeFile } from '@/types/node-types';
import { NodeStatusEvent } from '@/types/workflow-types';
import {
  CONSOLE_PANEL_WIDTH_KEY,
  CONSOLE_PANEL_MIN_WIDTH,
  CONSOLE_PANEL_MAX_WIDTH,
  DEFAULT_CONSOLE_PANEL_WIDTH,
  readStoredWidth,
} from '@/constants/workflow-editor';

export type WorkflowPanelTab =
  | 'events'
  | 'configure'
  | 'files'
  | 'evals'
  | 'mcp'
  | 'api'
  | 'errors'
  // Node-scoped source view for pinned deterministic custom-code nodes.
  | 'source';

interface PreviewData {
  result: Record<string, InputValue> | null | undefined;
  nodeId?: string;
  reactFlowGeneratedId?: string;
  statusEvents?: NodeStatusEvent[];
  isLoading: boolean;
  icon: string;
  displayName: string;
  nodeType: string;
}

interface FilesPreviewData {
  files: NodeFile[];
  icon?: string;
  displayName: string;
  nodeType?: string;
  isLoading?: boolean;
  error?: string | null;
}

export interface NodeDataPanelData {
  nodeData?: {
    result: Record<string, InputValue> | null | undefined;
    isLoading: boolean;
    nodeStatusEvents: NodeStatusEvent[];
  };
  nodeId?: string;
  expandAdvancedSettings?: boolean;
}

interface WorkflowPanelState {
  // Workflow panel state
  isSidebarOpen: boolean;
  isMaximized: boolean; // this controls whether panel is pinned to the edge of the screen
  isHoveringWhileMinimized: boolean; // Whether hovering over minimized sidebar button
  isConsolePanelOpen: boolean; // NOTE: this controls the floating panel open/closed state (relevant only when NOT maximized)
  isAddNodesOpen: boolean; // Node library occupying the same right-hand slot
  isWorkflowCardOpen: boolean; // Panel showing the workflow's own card on purpose
  panelSize: number; // Fixed pixel width (persisted to localStorage)
  activeTab: WorkflowPanelTab;
  nodePanelData?: NodeDataPanelData;
  // Each node card keeps its own tab, so opening Events on one node doesn't
  // move every other card off Configure. Kept here rather than in the sidebar
  // so it survives the sidebar unmounting behind an artifact preview.
  cardTabs: Record<string, WorkflowPanelTab>;

  // Preview panel state
  isPreviewVisible: boolean;
  previewData: PreviewData | null;

  // Files preview panel state
  isFilesPreviewVisible: boolean;
  filesPreviewData: FilesPreviewData | null;

  // Node status filtering
  filteredNodeId: string | null;

  // Published code from API
  mcpCode: string | null;
  apiCode: string | null;
  // Real MCP gateway endpoint from the publish response (set only when the
  // backend wired the workflow into the org's Published MCP server — null
  // means mcpCode is the legacy sample snippet).
  mcpEndpoint: string | null;

  setMcpCode: (mcpCode: string) => void;
  setApiCode: (apiCode: string) => void;
  /** Restore the persisted gateway endpoint on workflow reload without
   * resetting the tab-viewed notification state (unlike setPublishedCode). */
  setMcpEndpoint: (mcpEndpoint: string | null) => void;

  // Tab notification state (tracks if user has viewed new tabs)
  hasMcpTabBeenViewed: boolean;
  hasApiTabBeenViewed: boolean;

  // Actions
  openWorkflowPanel: (tab: WorkflowPanelTab, data?: NodeDataPanelData) => void;
  openWorkflowCard: (tab?: WorkflowPanelTab) => void;
  closeWorkflowPanel: () => void;
  setCardTab: (nodeId: string, tab: WorkflowPanelTab) => void;
  setActiveTab: (tab: WorkflowPanelTab) => void;
  markPublishTabViewed: (tab: WorkflowPanelTab) => void;
  toggleMaximized: () => void;
  setMaximized: (maximized: boolean) => void;
  setIsHoveringWhileMinimized: (isHovering: boolean) => void;
  setConsolePanelOpen: (isOpen: boolean) => void;
  openAddNodesPanel: () => void;
  closeAddNodesPanel: () => void;
  setPanelSize: (px: number) => void;
  showPreview: (data: PreviewData) => void;
  hidePreview: () => void;
  showFilesPreview: (data: FilesPreviewData) => void;
  updateFilesPreview: (updates: Partial<FilesPreviewData>) => void;
  hideFilesPreview: () => void;
  setFilteredNodeId: (nodeId: string | null) => void;

  // Publish actions
  setPublishedCode: (
    mcpCode?: string,
    apiCode?: string,
    mcpEndpoint?: string,
  ) => void;
  clearPublishedCode: () => void;

  // Scroll to error flag
  shouldScrollToError: boolean;
  setShouldScrollToError: (shouldScroll: boolean) => void;

  // Auto-scroll preference for console events (persists across console open/close)
  isAutoScrollEnabled: boolean;
  setIsAutoScrollEnabled: (enabled: boolean) => void;

  // Highlight field in configure tab
  highlightedFieldId: string | null;
  highlightedNodeId: string | null;
  setHighlightedField: (nodeId: string | null, fieldId: string | null) => void;

  // Events tab drill-down (independent of canvas selection)
  eventsDetailNodeId: string | null;
  setEventsDetailNodeId: (nodeId: string | null) => void;
}

// Every occupant of the right-hand slot evicts the others: they share one
// geometry, so a tenant that forgets to evict is drawn on top of another.
const SLOT_EVICTED = { isAddNodesOpen: false } as const;

const publishTabViewedUpdates = (
  state: WorkflowPanelState,
  tab: WorkflowPanelTab,
): Partial<WorkflowPanelState> => {
  if (tab === 'mcp' && !state.hasMcpTabBeenViewed) {
    return { hasMcpTabBeenViewed: true };
  }
  if (tab === 'api' && !state.hasApiTabBeenViewed) {
    return { hasApiTabBeenViewed: true };
  }
  return {};
};

export const useWorkflowPanelStore = create<WorkflowPanelState>((set) => ({
  // Workflow panel state
  isSidebarOpen: false,
  isMaximized: false,
  isHoveringWhileMinimized: false,
  isConsolePanelOpen: false,
  isAddNodesOpen: false,
  isWorkflowCardOpen: false,
  panelSize: readStoredWidth(
    CONSOLE_PANEL_WIDTH_KEY,
    DEFAULT_CONSOLE_PANEL_WIDTH,
  ),
  activeTab: 'events',
  cardTabs: {},
  chatData: undefined,

  // Preview panel state
  isPreviewVisible: false,
  previewData: null,

  // Files preview panel state
  isFilesPreviewVisible: false,
  filesPreviewData: null,

  // Node status filtering
  filteredNodeId: null,

  // Published code from API
  mcpCode: null,
  apiCode: null,
  mcpEndpoint: null,

  // Tab notification state
  hasMcpTabBeenViewed: false,
  hasApiTabBeenViewed: false,

  setMcpCode: (mcpCode: string) => set({ mcpCode }),
  setApiCode: (apiCode: string) => set({ apiCode }),
  setMcpEndpoint: (mcpEndpoint: string | null) => set({ mcpEndpoint }),

  // Workflow panel actions
  openWorkflowPanel: (tab: WorkflowPanelTab, data?: NodeDataPanelData) =>
    set((state) => ({
      ...SLOT_EVICTED,
      isSidebarOpen: true,
      activeTab: tab,
      nodePanelData: data,
      cardTabs: data?.nodeId
        ? { ...state.cardTabs, [data.nodeId]: tab }
        : state.cardTabs,
    })),

  // The gear button asks for the workflow's own card. Nothing is selected in
  // that state, so the flag is what keeps the panel from being read as a
  // leftover node inspector and closed.
  // The workflow card is whole-workflow scope, so a leftover Events drill-down
  // is cleared here rather than by each caller.
  openWorkflowCard: (tab = 'configure') =>
    set({
      ...SLOT_EVICTED,
      isSidebarOpen: true,
      activeTab: tab,
      isConsolePanelOpen: true,
      isWorkflowCardOpen: true,
      eventsDetailNodeId: null,
    }),

  closeWorkflowPanel: () =>
    set({
      isSidebarOpen: false,
      isMaximized: false,
      isHoveringWhileMinimized: false,
      nodePanelData: undefined,
      cardTabs: {},
    }),

  setCardTab: (nodeId: string, tab: WorkflowPanelTab) =>
    set((state) => ({ cardTabs: { ...state.cardTabs, [nodeId]: tab } })),

  toggleMaximized: () =>
    set((state) => ({
      isMaximized: !state.isMaximized,
      isAddNodesOpen: state.isMaximized ? state.isAddNodesOpen : false,
    })),

  setMaximized: (maximized: boolean) =>
    set((state) => ({
      isMaximized: maximized,
      isAddNodesOpen: maximized ? false : state.isAddNodesOpen,
      // Reset hover state when unpinning to ensure it goes to minimized state
      isHoveringWhileMinimized: maximized
        ? state.isHoveringWhileMinimized
        : false,
    })),

  setIsHoveringWhileMinimized: (isHovering: boolean) =>
    set({ isHoveringWhileMinimized: isHovering }),

  // The inspector and the node library share one slot, so every path that
  // opens one has to evict the other — including the node-store side effects
  // that fire on selection and never reach the components themselves.
  setConsolePanelOpen: (isOpen: boolean) =>
    set(
      isOpen
        ? {
            ...SLOT_EVICTED,
            isConsolePanelOpen: true,
            isWorkflowCardOpen: false,
          }
        : { isConsolePanelOpen: false, isWorkflowCardOpen: false },
    ),

  openAddNodesPanel: () =>
    set({
      isAddNodesOpen: true,
      isConsolePanelOpen: false,
      isMaximized: false,
      isHoveringWhileMinimized: false,
    }),

  closeAddNodesPanel: () => set({ isAddNodesOpen: false }),

  setPanelSize: (px: number) => {
    const clamped = Math.round(
      Math.max(CONSOLE_PANEL_MIN_WIDTH, Math.min(CONSOLE_PANEL_MAX_WIDTH, px)),
    );
    try {
      localStorage.setItem(CONSOLE_PANEL_WIDTH_KEY, String(clamped));
    } catch {
      // ignore
    }
    set({ panelSize: clamped });
  },

  setActiveTab: (tab: WorkflowPanelTab) =>
    set((state) => ({
      activeTab: tab,
      ...publishTabViewedUpdates(state, tab),
    })),

  // Stacked cards keep their own tab, so opening MCP or API on one of them has
  // to clear the badge without moving the shared tab.
  markPublishTabViewed: (tab: WorkflowPanelTab) =>
    set((state) => publishTabViewedUpdates(state, tab)),

  // Preview panel actions
  showPreview: (data: PreviewData) =>
    set({
      ...SLOT_EVICTED,
      isPreviewVisible: true,
      previewData: data,
      isConsolePanelOpen: false, // if right panel is not pinned/maximized, auto close it on open preview
    }),

  hidePreview: () => set({ isPreviewVisible: false, previewData: null }),

  // Files preview panel actions
  // Auto-close console panel when opening files preview to give more space
  showFilesPreview: (data: FilesPreviewData) =>
    set({
      ...SLOT_EVICTED,
      isFilesPreviewVisible: true,
      filesPreviewData: data,
      isConsolePanelOpen: false,
    }),

  updateFilesPreview: (updates: Partial<FilesPreviewData>) =>
    set((state) => ({
      filesPreviewData: state.filesPreviewData
        ? { ...state.filesPreviewData, ...updates }
        : null,
    })),

  hideFilesPreview: () =>
    set({ isFilesPreviewVisible: false, filesPreviewData: null }),

  // Node status filtering actions
  setFilteredNodeId: (nodeId: string | null) => set({ filteredNodeId: nodeId }),

  // Publish actions
  setPublishedCode: (
    mcpCode?: string,
    apiCode?: string,
    mcpEndpoint?: string,
  ) =>
    set({
      mcpCode: mcpCode || null,
      apiCode: apiCode || null,
      mcpEndpoint: mcpEndpoint || null,
      // Reset viewed state when newly published
      hasMcpTabBeenViewed: false,
      hasApiTabBeenViewed: false,
    }),

  clearPublishedCode: () =>
    set({
      mcpCode: null,
      apiCode: null,
      mcpEndpoint: null,
      hasMcpTabBeenViewed: true,
      hasApiTabBeenViewed: true,
    }),

  // Scroll to error flag
  shouldScrollToError: false,
  setShouldScrollToError: (shouldScroll: boolean) =>
    set({ shouldScrollToError: shouldScroll }),

  // Auto-scroll preference for console events (persists across console open/close)
  isAutoScrollEnabled: true,
  setIsAutoScrollEnabled: (enabled: boolean) =>
    set({ isAutoScrollEnabled: enabled }),

  // Highlight field in configure tab
  highlightedFieldId: null,
  highlightedNodeId: null,
  setHighlightedField: (nodeId: string | null, fieldId: string | null) =>
    set({ highlightedNodeId: nodeId, highlightedFieldId: fieldId }),

  // Events tab drill-down
  eventsDetailNodeId: null,
  setEventsDetailNodeId: (nodeId: string | null) =>
    set({ eventsDetailNodeId: nodeId }),
}));
